import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { COMPANY } from "@/lib/company";
import { Link } from "react-router-dom";

interface Section {
  title: string;
  items: string[];
  /** 항목 뒤에 붙는 표. 값끼리 나란히 놓여야 뜻이 통하는 곳(보유 기간)에만 쓴다. */
  table?: { head: string[]; rows: string[][] };
}

/**
 * 링크 수명과 대화 파기 주기를 떨어뜨려 적으면 "링크가 닫혔으니 기록도 없겠지" 로
 * 읽힌다 — 실제로는 링크가 닫힌 뒤에도 대화는 남는다. 그래서 한 표에 둔다.
 * 숫자의 원본은 API 설정이다: `app.guest_link_expire_days`(14) ·
 * `app.visitor_retention_days`(90). 설정을 바꾸면 여기도 같이 고쳐야 하고,
 * `lib/privacy-visitor.test.tsx` 가 둘을 대조해 CI 에서 잠근다.
 */
const RETENTION_TABLE: NonNullable<Section["table"]> = {
  head: ["구분", "대상", "보유·파기 기준"],
  rows: [
    [
      "게스트 안내 링크(챗봇 접속)",
      "예약 게스트",
      "체크아웃 후 14일이 지나면 링크가 열리지 않습니다",
    ],
    [
      "게스트 대화 기록",
      "예약 게스트",
      "회원이 해당 게스트를 삭제할 때 함께 파기합니다 (자동 파기 주기 없음)",
    ],
    [
      "방문자 세션·대화 기록",
      "공개 챗봇 방문자",
      "마지막 이용일·작성일부터 90일이 지나면 자동 파기합니다",
    ],
  ],
};

const SECTIONS: Section[] = [
  {
    title: "제1조 (개인정보의 처리 목적)",
    items: [
      "회원 관리: 관리자 계정의 가입·본인 확인, 로그인 및 계정 유지, 서비스 부정 이용 방지.",
      "서비스 제공: 게스트 챗봇 컨시어지 운영, 예약·객실 관리, 채널(OTA) 연동, 리포트 제공.",
      "공개 숙소 챗봇 운영: 회원이 공개한 숙소 챗봇 링크로 들어온 방문자의 문의에 답변하고, 같은 방문자가 다시 들어왔을 때 이전 대화를 이어 보여줍니다.",
      "고객 문의 대응 및 서비스 개선을 위한 통계 분석.",
      "안내 공백 분석: 챗봇 대화 중 게스트·방문자의 질문이 등록된 안내 항목에 매칭되었는지를 집계해, 해당 숙소를 운영하는 회원에게 리포트로 제공합니다. 매칭되지 않은 질문은 원문 그대로 표시되며, 이는 해당 숙소의 회원에게만 보이고 다른 회원이나 제3자에게는 제공되지 않습니다.",
    ],
  },
  {
    title: "제2조 (처리하는 개인정보의 항목)",
    items: [
      "관리자 회원: 이메일 주소, 이름, 비밀번호(이메일 로그인 시) 또는 Google 계정 식별 정보(Google 로그인 시).",
      "게스트: 예약자 성명, 이메일 주소, 전화번호, 사용 언어, 예약 정보(체크인·체크아웃 일자, 객실 정보), 챗봇 대화 내용. 게스트 정보는 회원이 직접 등록하거나 OTA(온라인 여행사)를 통해 수집됩니다.",
      "공개 챗봇 방문자: 방문자가 대화를 시작하며 직접 입력한 이름(호칭으로 쓰이며 실명인지 확인하지 않습니다), 선택한 언어, 챗봇 대화 내용, 최초 이용 일시와 마지막 이용 일시. 예약이 없는 익명 이용이므로 회사는 방문자의 이메일 주소·전화번호·예약 정보를 수집하지 않으며, 접속 IP 주소나 브라우저 정보(User-Agent)도 수집하지 않습니다. 다시 들어왔을 때 이전 대화를 이어 보여주기 위한 세션 식별자는 회사가 부여한 임의의 값이고 방문자의 브라우저에 저장되며, 이를 다른 숙소의 이용 기록이나 다른 정보와 결합하지 않습니다.",
      "숙박 계약 체결 기록: 숙소가 계약서를 등록한 경우에 한해, 게스트가 동의한 계약서 본문과 동의 시각, 표시된 계약서 언어 및 화면 언어, 동의 방식(안내 링크 화면의 동의 버튼)을 함께 보존합니다. 「전자문서 및 전자거래 기본법」 제5조가 요구하는 열람 보장과 송수신 확인을 위한 것이며, 접속 IP 주소나 브라우저 정보(User-Agent)는 수집하지 않습니다.",
      "자동 수집 항목: 접속 일시, 서비스 이용 기록.",
    ],
  },
  {
    title: "제3조 (개인정보의 처리 및 보유 기간)",
    items: [
      "회원 정보: 회원 탈퇴 시까지. 다만 관련 법령에 따라 보존할 필요가 있는 경우 해당 기간 동안 보존합니다.",
      "게스트 정보 및 대화 기록: 서비스 제공 목적 달성 시 또는 회원의 삭제 요청 시까지. 체크아웃 이후 일정 기간이 지나면 게스트 챗 링크는 열리지 않지만, 대화 기록 자체는 예약에 딸린 기록이므로 회원이 해당 게스트를 삭제할 때 함께 파기됩니다(별도의 자동 삭제 주기는 두고 있지 않습니다).",
      "공개 챗봇 방문자의 세션 및 대화 기록: 마지막 이용일 또는 대화 작성일로부터 90일이 지나면 회사가 자동으로 파기합니다. 방문자의 기록은 예약에 딸려 있지 않아 회원이 삭제할 대상이 없으므로, 별도의 요청이 없어도 파기되도록 보유 기간을 정해 두었습니다.",
      "「전자상거래 등에서의 소비자보호에 관한 법률」 등 관련 법령에 따른 보존: 계약 또는 청약철회 등에 관한 기록 5년, 대금결제 및 재화 등의 공급에 관한 기록 5년, 소비자의 불만 또는 분쟁처리에 관한 기록 3년.",
      "챗봇 접속 링크의 수명과 대화 기록의 파기 기준을 한눈에 보면 다음과 같습니다.",
    ],
    table: RETENTION_TABLE,
  },
  {
    title: "제4조 (개인정보의 제3자 제공 및 처리위탁)",
    items: [
      "회사는 정보주체의 동의 또는 법령의 규정이 있는 경우를 제외하고 개인정보를 제3자에게 제공하지 않습니다.",
      "회사는 원활한 서비스 제공을 위해 다음과 같이 개인정보 처리 업무를 위탁하고 있습니다. ① OpenAI, L.L.C. — AI 답변 생성: 챗봇 대화 내용과 답변에 필요한 이용자 이름(게스트는 투숙객 성명, 방문자는 본인이 입력한 이름)·객실·체크인·체크아웃 일자를 처리합니다. 방문자는 예약 정보가 없으므로 대화 내용과 이름만 전송됩니다. ② Anthropic PBC — AI 답변 생성(대체 경로): ①의 호출이 실패한 경우에 한해 같은 항목을 대신 처리합니다. ③ Channex Ltd. — 채널(OTA) 연동: OTA 예약의 예약자 성명·이메일 주소·전화번호·사용 언어·숙박 일정을 예약 동기화 목적으로 처리합니다. ④ Google LLC — 관리자 Google 로그인 인증: 관리자의 이메일 주소·이름·Google 계정 식별자를 처리합니다.",
      "위탁 계약 시 개인정보 보호 관련 법령을 준수하도록 관리·감독하며, 위탁 업무 내용이나 수탁자가 변경될 경우 본 처리방침을 통해 공개합니다.",
      "위 위탁에는 개인정보의 국외 이전이 포함되며, 상세는 다음과 같습니다. ① 이전받는 자: OpenAI, L.L.C. / 이전 국가: 미국 / 이전 항목: 챗봇 대화 내용, 이용자 이름(투숙객 성명 또는 방문자가 입력한 이름), 객실·체크인·체크아웃 일자(게스트에 한함) / 이전 시기·방법: 게스트 또는 방문자가 챗봇에 메시지를 보낼 때마다 암호화된(HTTPS) API 통신으로 전송 / 이용 목적: AI 답변 생성 / 보유·이용 기간: 답변 생성에 필요한 시점까지이며, 회사는 이전받는 자에게 별도 보관이나 모델 학습 목적의 이용을 요청하지 않습니다. ② 이전받는 자: Anthropic PBC / 이전 국가: 미국 / 이전 항목: 챗봇 대화 내용, 이용자 이름(투숙객 성명 또는 방문자가 입력한 이름), 객실·체크인·체크아웃 일자(게스트에 한함) / 이전 시기·방법: ①의 호출이 실패한 경우에 한해 암호화된(HTTPS) API 통신으로 전송 / 이용 목적: AI 답변 생성(대체 경로) / 보유·이용 기간: 답변 생성에 필요한 시점까지이며, 회사는 이전받는 자에게 별도 보관이나 모델 학습 목적의 이용을 요청하지 않습니다. ③ 이전받는 자: Channex Ltd. / 이전 국가: 영국 / 이전 항목: 예약자 성명, 이메일 주소, 전화번호, 사용 언어, 숙박 일정 / 이전 시기·방법: OTA 예약이 생성·변경될 때 암호화된(HTTPS) API 및 웹훅 통신 / 이용 목적: 채널(OTA) 예약 연동 / 보유·이용 기간: 위탁 계약 종료 시까지. ④ 이전받는 자: Google LLC / 이전 국가: 미국 / 이전 항목: 관리자 이메일 주소, 이름, Google 계정 식별자 / 이전 시기·방법: 관리자가 Google 로그인을 이용하는 시점에 암호화된(HTTPS) 통신 / 이용 목적: 관리자 로그인 인증 / 보유·이용 기간: 관리자 계정 삭제 시까지.",
      "회사가 Channex Ltd. 로 내보내는 정보는 숙소·객실·요금·재고 정보에 한하며, 게스트의 개인정보를 Channex 로 전송하지는 않습니다. OTA 예약자 정보는 Channex 를 통해 회사가 전달받습니다.",
      "정보주체는 개인정보의 국외 이전을 거부할 수 있으며, 거부를 원하는 경우 제8조의 개인정보 보호책임자 연락처로 요청할 수 있습니다. 다만 AI 답변 생성과 채널 연동은 서비스의 핵심 기능이므로, 이전을 거부하면 챗봇 응답이나 OTA 예약 연동 이용이 제한될 수 있습니다.",
    ],
  },
  {
    title: "제5조 (정보주체의 권리·의무 및 행사 방법)",
    items: [
      "정보주체는 회사에 대해 언제든지 개인정보의 열람, 정정, 삭제, 처리정지를 요구할 수 있습니다.",
      `권리 행사는 개인정보 보호책임자에게 서면 또는 이메일(${COMPANY.privacyContact})로 요청할 수 있으며, 회사는 지체 없이 조치합니다.`,
      "정보주체는 대리인(법정대리인 또는 위임을 받은 자)을 통해 권리를 행사할 수 있습니다.",
      "공개 챗봇 방문자는 회사가 신원을 확인할 수 있는 정보를 보유하지 않으므로, 열람·삭제를 요청할 때에는 이용한 숙소와 대화 시점처럼 해당 대화를 특정할 수 있는 정보를 함께 알려주셔야 합니다. 확인되지 않는 경우에도 제3조의 보유 기간이 지나면 자동으로 파기됩니다.",
    ],
  },
  {
    title: "제6조 (개인정보의 파기)",
    items: [
      "회사는 개인정보 보유 기간의 경과, 처리 목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체 없이 해당 개인정보를 파기합니다.",
      "전자적 파일 형태의 정보는 복구할 수 없는 방법으로 영구 삭제하며, 종이 문서는 분쇄하거나 소각하여 파기합니다.",
    ],
  },
  {
    title: "제7조 (개인정보의 안전성 확보 조치)",
    items: [
      "비밀번호의 일방향 암호화 저장 및 전송 구간 암호화(HTTPS) 적용.",
      "접근 권한의 관리: 개인정보를 처리하는 계정에 대한 접근 권한을 최소한으로 부여하고 관리합니다.",
      "접속 기록의 보관 및 위·변조 방지 조치.",
    ],
  },
  {
    title: "제8조 (개인정보 보호책임자)",
    items: [
      `개인정보 보호책임자: ${COMPANY.privacyOfficer}`,
      `연락처: ${COMPANY.privacyContact}`,
      "정보주체는 서비스 이용 중 발생한 모든 개인정보 보호 관련 문의, 불만 처리, 피해 구제 등에 관한 사항을 개인정보 보호책임자에게 문의할 수 있습니다.",
      "기타 개인정보 침해에 대한 신고나 상담이 필요한 경우 개인정보침해신고센터(privacy.kisa.or.kr, 국번 없이 118), 개인정보분쟁조정위원회(kopico.go.kr, 1833-6972)에 문의할 수 있습니다.",
    ],
  },
  {
    title: "제9조 (개인정보 처리방침의 변경)",
    items: [
      "이 개인정보 처리방침의 내용이 추가, 삭제 또는 수정되는 경우 시행 7일 전부터 서비스 내 공지사항을 통해 고지합니다.",
    ],
  },
];

/** 개인정보 처리방침 — 푸터 및 로그인 화면에서 링크됨. */
export function PrivacyPage() {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6 md:py-16">
      <header className="doc-head">
        <h1 className="doc-title">개인정보 처리방침</h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          {COMPANY.name}(이하 "회사")는 「개인정보 보호법」 등 관련 법령을 준수하며, 정보주체의
          개인정보를 보호하기 위해 다음과 같이 개인정보 처리방침을 수립·공개합니다. 회원(관리자)이
          서비스에 로그인하면{" "}
          <Link to="/terms" className="font-medium text-primary underline underline-offset-2">
            이용약관
          </Link>{" "}
          및 본 방침에 동의하는 것으로 간주됩니다. 게스트는 별도의 로그인 없이 숙소가 발급한 링크로
          챗봇을 이용하므로, 회사는 챗 화면에서 대화 내용의 처리 목적·AI 답변 생성·국외 이전 사실을
          고지하고 본 방침으로 안내합니다. 예약 없이 공개된 숙소 챗봇 링크로 들어온 방문자에게도
          같은 화면에서 같은 내용을 고지합니다.
        </p>
      </header>

      <div className="mt-10 space-y-8">
        {SECTIONS.map((section) => (
          <section key={section.title} className="animate-rise">
            <h2 className="text-base font-semibold text-foreground">{section.title}</h2>
            <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground">
              {section.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
            {section.table ? (
              <div className="mt-4 rounded-lg border border-border/60 text-muted-foreground">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {section.table.head.map((cell) => (
                        <TableHead key={cell}>{cell}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {section.table.rows.map((row) => (
                      <TableRow key={row[0]}>
                        {row.map((cell) => (
                          <TableCell key={cell} className="align-top leading-relaxed">
                            {cell}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </section>
        ))}
      </div>

      <p className="mt-12 border-t border-border/60 pt-6 text-sm text-muted-foreground">
        부칙: 이 방침은 {COMPANY.privacyEffectiveDate}부터 시행합니다.
      </p>
    </div>
  );
}
